import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import { PermissionService } from 'src/app/core/services/permission.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { WorkspaceDetailsDto, WorkspaceDocumentDto } from '../../models/workspaces.model';
import { selectWorkspaceDetails, selectWorkspaceDetailsLoading } from '../../store/selectors';
import { WorkspacesActions } from '../../store/actions';
import { DocumentsApiService } from 'src/app/modules/documents/services/documents-api.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DocSendReportModalComponent } from 'src/app/modules/documents/components/modals/doc-send-report-modal/doc-send-report-modal.component';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
  selector: 'app-clause-variance-analysis-box',
  templateUrl: './clause-variance-analysis-box.component.html',
  styleUrl: './clause-variance-analysis-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClauseVarianceAnalysisBoxComponent implements OnInit {
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public isWorkspaceDetailsLoading$ = this.store$.select(selectWorkspaceDetailsLoading);
  public workspaceDetails: WorkspaceDetailsDto | null = null;
  public form: FormGroup = this.fb.group({
    referenceDocument: [null as string | null],
  });
  public selectedReferenceDocument: WorkspaceDocumentDto | null = null;
  public canReadVarianceAnalysis = false;
  public canReadDocumentProcessing = false;

  constructor(
    private readonly store$: Store,
    private readonly cdr: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly documentsApiService: DocumentsApiService,
    private readonly bsModalService: BsModalService,
    private readonly router: Router,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    this.startInitialization();
  }

  startInitialization(): void {
    combineLatest([
      this.store$.select(selectWorkspaceDetails),
      this.permissionService.canRead$(PermissionResource.VarianceAnalysis),
      this.permissionService.canRead$(PermissionResource.DocumentProcessing),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([workspaceDetails, canReadVarianceAnalysis, canReadDocumentProcessing]) => {
        this.workspaceDetails = workspaceDetails;
        this.canReadVarianceAnalysis = canReadVarianceAnalysis;
        this.canReadDocumentProcessing = canReadDocumentProcessing;
        this.cdr.markForCheck();
      });
  }

  launchAnalysis(): void {
    if (!this.canLaunchAnalysis) {
      return;
    }

    this.router.navigate([`/workspaces/${this.workspaceDetails?.id}/clause-variance-analysis`]);
  }

  generateReport(): void {
    if (!this.selectedReferenceDocument) {
      return;
    }

    this.documentsApiService
      .getDocumentById$(this.selectedReferenceDocument.id)
      .pipe(untilDestroyed(this))
      .subscribe((document) => {
        this.bsModalService.show(DocSendReportModalComponent, {
          initialState: {
            document,
            clauseVarianceMode: true,
          },
          class: 'modal-lg',
        });
      });
  }

  onReferenceDocumentSelect(docId: string): void {
    if (!docId) {
      this.selectedReferenceDocument = null;
      this.store$.dispatch(
        WorkspacesActions.setReferenceDocument({
          referenceDocument: null,
        }),
      );
      return;
    }

    this.selectedReferenceDocument =
      this.workspaceDocuments.find((doc) => doc.id === docId) || this.workspaceDocuments[0];

    this.store$.dispatch(
      WorkspacesActions.setReferenceDocument({
        referenceDocument: this.selectedReferenceDocument,
      }),
    );
  }

  get workspaceDocuments(): WorkspaceDocumentDto[] {
    return this.workspaceDetails?.documents || [];
  }

  get canLaunchAnalysis(): boolean {
    return this.canReadVarianceAnalysis && this.canReadDocumentProcessing;
  }
}
